import { useRouter } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { StaffNotification } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useReadAllNotifications, useReadNotification } from '../hooks/use-notification-mutations';
import { useStaffNotifications } from '../hooks/use-staff-notifications';
import { notificationTarget } from '../schemas/internal-link';

/**
 * In-app alerts of the signed-in member (stage delays today). The count polls every minute;
 * opening one marks it read and goes to the order it is about. Older alerts load on demand and
 * the list can be narrowed to unread ones.
 */
export function NotificationBell() {
  const { t } = useTranslation('production');
  const format = useFormat();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const query = useStaffNotifications(unreadOnly);
  const read = useReadNotification();
  const readAll = useReadAllNotifications();
  const unread = query.data?.pages[0]?.unreadCount ?? 0;
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  const follow = (notification: StaffNotification) => {
    if (!notification.readAt) read.mutate(notification.id);
    setOpen(false);
    const target = notificationTarget(notification);
    if (target) router.history.push(target);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={t('bell.label', { count: unread })}
          data-tour="shell.notifications"
        >
          <Bell aria-hidden />
          {unread > 0 ? (
            <span
              aria-hidden
              data-testid="bell-count"
              className="bg-destructive absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white tabular-nums"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <p className="text-sm font-semibold">{t('bell.title')}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={unread === 0 || readAll.isPending}
            onClick={() => readAll.mutate()}
          >
            {t('bell.readAll')}
          </Button>
        </div>
        <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
          <Label htmlFor="bell-unread-only" className="text-muted-foreground text-xs">
            {t('bell.unreadOnly')}
          </Label>
          <Switch id="bell-unread-only" checked={unreadOnly} onCheckedChange={setUnreadOnly} />
        </div>
        {query.isError ? (
          <p className="text-muted-foreground p-3 text-sm">{t('bell.loadError')}</p>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground p-3 text-sm">
            {unreadOnly ? t('bell.noUnread') : t('bell.empty')}
          </p>
        ) : (
          <ul className="max-h-96 overflow-y-auto" aria-label={t('bell.listLabel')}>
            {items.map((notification) => (
              <li key={notification.id} className="border-b last:border-b-0">
                <button
                  type="button"
                  onClick={() => follow(notification)}
                  className={cn(
                    'hover:bg-muted flex w-full items-start gap-2 px-3 py-2 text-left text-sm',
                    !notification.readAt && 'bg-primary/5',
                  )}
                >
                  <span
                    className={cn(
                      'mt-1.5 size-2 shrink-0 rounded-full',
                      notification.readAt ? 'bg-transparent' : 'bg-primary',
                    )}
                    aria-hidden
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className={cn(!notification.readAt && 'font-medium')}>
                      {notification.title}
                      {notification.readAt ? null : (
                        <span className="sr-only"> ({t('bell.unread')})</span>
                      )}
                    </span>
                    {notification.body ? (
                      <span className="text-muted-foreground text-xs">{notification.body}</span>
                    ) : null}
                    <span
                      className="text-muted-foreground text-xs"
                      title={format.utc(notification.createdAt)}
                    >
                      {format.relative(notification.createdAt)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {query.hasNextPage ? (
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? t('bell.loadingMore') : t('bell.loadMore')}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
